import React from 'react'
import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core'

import type { StepEditPanelProps } from '../pipelineBuilder.types'
import type { ScreeningStepConfig } from '../stepConfig.types'

const REPORT_OPTIONS = [
    { value: 'credit', label: 'Credit' },
    { value: 'background', label: 'Background' },
    { value: 'income', label: 'Income' },
] as const

type ScreeningReport = typeof REPORT_OPTIONS[number]['value']

export const ScreeningStepPanel: React.FC<StepEditPanelProps> = ({
    step,
    onChange,
}) => {
    const config = (step.config ?? {}) as ScreeningStepConfig
    const reports: ScreeningReport[] = config.reports ?? [
        'credit',
        'background',
        'income',
    ]

    const patchConfig = (patch: Partial<ScreeningStepConfig>) => {
        onChange({
            config: {
                ...config,
                ...patch,
            },
        })
    }

    const toggleReport = (report: ScreeningReport) => {
        const next = reports.includes(report)
            ? reports.filter((item) => item !== report)
            : [...reports, report]
        patchConfig({ reports: next })
    }

    return (
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <TextField
                label="Instance label"
                size="small"
                variant="outlined"
                fullWidth
                value={config.instance_label ?? ''}
                onChange={(e) =>
                    patchConfig({
                        instance_label: e.target.value || undefined,
                    })
                }
            />

            <Typography variant="subtitle2">Reports</Typography>
            {REPORT_OPTIONS.map((option) => (
                <FormControlLabel
                    key={option.value}
                    control={
                        <Switch
                            checked={reports.includes(option.value)}
                            onChange={() => toggleReport(option.value)}
                            color="primary"
                        />
                    }
                    label={option.label}
                />
            ))}

            <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Data source</InputLabel>
                <Select
                    label="Data source"
                    value={config.data_source ?? 'workbook'}
                    onChange={(e) =>
                        patchConfig({
                            data_source: e.target
                                .value as ScreeningStepConfig['data_source'],
                        })
                    }
                >
                    <MenuItem value="workbook">Application workbook</MenuItem>
                    <MenuItem value="application">Signed application</MenuItem>
                    <MenuItem value="manual">Manual entry</MenuItem>
                </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Required workbook phase</InputLabel>
                <Select
                    label="Required workbook phase"
                    value={config.required_phase ?? 'identity_complete'}
                    onChange={(e) =>
                        patchConfig({
                            required_phase: e.target
                                .value as ScreeningStepConfig['required_phase'],
                        })
                    }
                >
                    <MenuItem value="identity_complete">
                        Identity complete
                    </MenuItem>
                    <MenuItem value="workbook_complete">
                        Workbook complete
                    </MenuItem>
                </Select>
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={config.scoring ?? true}
                        onChange={(e) =>
                            patchConfig({ scoring: e.target.checked })
                        }
                        color="primary"
                    />
                }
                label="Apply EZOS scoring rules"
            />
        </Box>
    )
}
